import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Plus, Pencil, Trash2, Upload, AlertTriangle,
  Loader2, Book, Search, ChevronDown, ChevronUp,
} from 'lucide-react'
import {
  Button, Input, Modal, EmptyState, LoadingState, ErrorState,
  DataTable, type ColumnDef, StatusBadge, Badge, Select, IconButton,
} from '@/components/ui'
import { ItemFormModal } from '@/components/service-catalog/ItemFormModal.tsx'
import type { ServiceCatalogCategory, ServiceCatalogItem } from '@/components/service-catalog/ItemFormModal.tsx'

// ─── Types ───────────────────────────────────────────────────────────────────

interface ServiceCatalogListResponse {
  data: ServiceCatalogItem[]
  total: number
  page: number
  pageSize: number
}

interface SapTenant {
  id: string
  code: string
  name: string
}

// ─── SAP Push Cell ────────────────────────────────────────────────────────────

function SapStatusCell({
  item,
  sapTenants,
  syncTenantIds,
  onPushed,
}: {
  item: ServiceCatalogItem
  sapTenants: SapTenant[]
  syncTenantIds: string[]
  onPushed: () => void
}) {
  const [pushing, setPushing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorExpanded, setErrorExpanded] = useState(false)
  const [tenantErrors, setTenantErrors] = useState<{ tenantId: string; status: string; errorMessage?: string }[]>([])

  async function push() {
    if (syncTenantIds.length === 0) {
      setError('Keine Sync-Mandanten konfiguriert. Bitte in Einstellungen → Artikel → Servicekatalog festlegen.')
      return
    }
    setPushing(true)
    setError(null)
    setTenantErrors([])
    try {
      const res = await apiClient.post(`/service-catalog/${item.id}/push-sap`, {})
      const d = res.data as { ok: boolean; tenantResults?: { tenantId: string; status: string; errorMessage?: string }[] }
      const failures = (d.tenantResults ?? []).filter((r) => r.status === 'failed')
      if (failures.length > 0) {
        setError(`SAP-Push fehlgeschlagen für ${failures.length} Mandant(en)`)
        setTenantErrors(failures)
      }
      onPushed()
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setError(e.response?.data?.error ?? e.message ?? 'Push fehlgeschlagen')
    } finally {
      setPushing(false)
    }
  }

  const statuses = item.sapTenantStatuses ?? []
  const configuredCount = syncTenantIds.length
  const syncedCount = statuses.filter((s) => s.status === 'synced').length
  const failedCount = statuses.filter((s) => s.status === 'failed').length
  const hasTenantData = statuses.length > 0 && configuredCount > 0
  const persistedErrors = statuses.filter((s) => s.status === 'failed' && s.errorMessage)

  if (configuredCount === 0) {
    return <span className="text-xs text-gray-400">—</span>
  }

  const sapBadgeStatus = !hasTenantData
    ? 'neutral'
    : failedCount > 0
      ? 'failed'
      : syncedCount === configuredCount
        ? 'success'
        : 'warning'

  const sapBadgeLabel = !hasTenantData
    ? 'nicht gepusht'
    : failedCount > 0
      ? `${failedCount} Fehler`
      : `${syncedCount}/${configuredCount}`

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <StatusBadge status={sapBadgeStatus} label={sapBadgeLabel} />
        {item.sapItemCode && (
          <span className="text-xs font-mono text-gray-400 truncate max-w-[60px]" title={item.sapItemCode}>
            {item.sapItemCode}
          </span>
        )}
        <IconButton
          icon={pushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          aria-label={hasTenantData ? 'In SAP aktualisieren' : 'In SAP anlegen'}
          variant="ghost"
          size="sm"
          onClick={push}
          disabled={pushing}
          className="ml-auto"
        />
      </div>

      {(error || persistedErrors.length > 0) && (
        <div className="rounded border border-red-200 bg-red-50 p-1.5">
          <button
            onClick={() => setErrorExpanded((v) => !v)}
            className="flex w-full items-start gap-1 text-left"
          >
            <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" />
            <span className="text-xs text-red-700 flex-1 leading-snug">
              {error ?? `SAP-Fehler für ${persistedErrors.length} Mandant(en)`}
            </span>
            {(tenantErrors.length > 0 || persistedErrors.length > 0) && (
              errorExpanded
                ? <ChevronUp className="w-3 h-3 text-red-400 shrink-0" />
                : <ChevronDown className="w-3 h-3 text-red-400 shrink-0" />
            )}
          </button>
          {errorExpanded && (
            <div className="mt-1 space-y-0.5 border-t border-red-200 pt-1">
              {(tenantErrors.length > 0 ? tenantErrors : persistedErrors).map((e) => {
                const t = sapTenants.find((t) => t.id === e.tenantId)
                return (
                  <p key={e.tenantId} className="text-xs text-red-600 font-mono break-all">
                    <span className="font-sans font-medium">{t?.code ?? e.tenantId}: </span>
                    {e.errorMessage ?? 'Push fehlgeschlagen'}
                  </p>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main Section ─────────────────────────────────────────────────────────────

export function ServicekatalogSection() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterStatus, setFilterStatus] = useState<'' | 'active' | 'inactive'>('')
  const [page, setPage] = useState(1)
  const [editItem, setEditItem] = useState<ServiceCatalogItem | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<ServiceCatalogItem | null>(null)

  const PAGE_SIZE = 25

  const { data: categories = [] } = useQuery<ServiceCatalogCategory[]>({
    queryKey: ['service-catalog-categories'],
    queryFn: () => apiClient.get('/service-catalog/categories').then((r) => r.data),
    retry: false,
  })

  const { data: sapTenants = [] } = useQuery<SapTenant[]>({
    queryKey: ['article-groups-sap-tenants'],
    queryFn: () => apiClient.get('/articles/groups/sap-tenants').then((r) => r.data),
    retry: false,
  })

  const { data: settings } = useQuery<{ defaultUnit: string; syncTenantIds: string[] }>({
    queryKey: ['service-catalog-settings'],
    queryFn: () => apiClient.get('/service-catalog/settings').then((r) => r.data),
    retry: false,
  })
  const defaultUnit = settings?.defaultUnit ?? 'Stk'
  const syncTenantIds = settings?.syncTenantIds ?? []

  const { data, isLoading, error } = useQuery<ServiceCatalogListResponse>({
    queryKey: ['service-catalog-items', search, filterCategory, filterStatus, page],
    queryFn: () =>
      apiClient.get('/service-catalog', {
        params: {
          q: search || undefined,
          categoryId: filterCategory || undefined,
          isActive: filterStatus === 'active' ? 'true' : filterStatus === 'inactive' ? 'false' : undefined,
          page,
          pageSize: PAGE_SIZE,
        },
      }).then((r) => r.data),
    retry: false,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/service-catalog/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog-items'] })
      setDeleteConfirm(null)
    },
  })

  const formatEur = (cents: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100)

  const columns: ColumnDef<ServiceCatalogItem>[] = [
    {
      id: 'title',
      header: 'Bezeichnung',
      accessorFn: (r) => r.title,
      cell: ({ row }) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{row.original.title}</p>
          {row.original.description && (
            <p className="text-xs text-gray-500 truncate max-w-xs">{row.original.description}</p>
          )}
        </div>
      ),
      size: 300,
    },
    {
      id: 'category',
      header: 'Kategorie',
      accessorFn: (r) => r.categoryName ?? '—',
      cell: ({ row }) => (
        row.original.categoryName
          ? <Badge variant="info">{row.original.categoryName}</Badge>
          : <span className="text-xs text-gray-400">—</span>
      ),
      size: 170,
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (r) => r.isActive,
      cell: ({ row }) => (
        <StatusBadge status={row.original.isActive ? 'active' : 'inactive'} />
      ),
      size: 90,
    },
    {
      id: 'price',
      header: 'Preis',
      accessorFn: (r) => r.priceCents,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-gray-900">
          {formatEur(row.original.priceCents)} / {row.original.unit}
        </span>
      ),
      size: 130,
    },
    {
      id: 'time',
      header: 'Zeitaufwand',
      accessorFn: (r) => r.timeHours,
      cell: ({ row }) => (
        <span className="text-sm text-gray-600">
          {row.original.timeHours} {row.original.unit}
        </span>
      ),
      size: 110,
    },
    {
      id: 'sap',
      header: 'SAP-Status',
      cell: ({ row }) => (
        <SapStatusCell
          item={row.original}
          sapTenants={sapTenants}
          syncTenantIds={syncTenantIds}
          onPushed={() => queryClient.invalidateQueries({ queryKey: ['service-catalog-items'] })}
        />
      ),
      size: 160,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          <IconButton
            icon={<Pencil className="w-3.5 h-3.5" />}
            aria-label="Bearbeiten"
            variant="ghost"
            size="sm"
            onClick={() => { setEditItem(row.original); setFormOpen(true) }}
          />
          <IconButton
            icon={<Trash2 className="w-3.5 h-3.5" />}
            aria-label="Löschen"
            variant="ghost"
            size="sm"
            onClick={() => setDeleteConfirm(row.original)}
            className="text-gray-400 hover:text-red-500"
          />
        </div>
      ),
      size: 70,
    },
  ]

  const items = data?.data ?? []
  const total = data?.total ?? 0

  const categoryOptions = [
    { value: '', label: 'Alle Kategorien' },
    ...categories.map((c) => ({ value: c.id, label: c.name })),
  ]

  const statusOptions = [
    { value: '', label: 'Alle Status' },
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
  ]

  if (isLoading) return <LoadingState message="Servicekatalog wird geladen…" />
  if (error) {
    const e = error as { response?: { data?: { error?: string } }; message?: string }
    return <ErrorState message={e.response?.data?.error ?? e.message ?? 'Fehler beim Laden'} />
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Book className="w-5 h-5 text-brand-500" />
            Servicekatalog
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {total} Einträge · Leistungen mit KI anlegen und in SAP synchronisieren
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => { setEditItem(null); setFormOpen(true) }}
        >
          <Plus className="w-4 h-4 mr-1" />
          Neuer Artikel
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Titel oder Artikelnummer suchen…"
            className="pl-9"
          />
        </div>
        <div className="w-48">
          <Select
            value={filterCategory}
            onValueChange={(v) => { setFilterCategory(v); setPage(1) }}
            options={categoryOptions}
            placeholder="Alle Kategorien"
          />
        </div>
        <div className="w-36">
          <Select
            value={filterStatus}
            onValueChange={(v) => { setFilterStatus(v as '' | 'active' | 'inactive'); setPage(1) }}
            options={statusOptions}
            placeholder="Alle Status"
          />
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <EmptyState
          title="Keine Einträge gefunden"
          description={
            search || filterCategory || filterStatus
              ? 'Keine Ergebnisse für die gewählten Filter.'
              : 'Erstelle den ersten Servicekatalog-Eintrag mit dem Button oben.'
          }
          action={
            !search && !filterCategory && !filterStatus ? (
              <Button variant="primary" size="sm" onClick={() => { setEditItem(null); setFormOpen(true) }}>
                <Plus className="w-4 h-4 mr-1" />
                Ersten Artikel anlegen
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          pageSize={PAGE_SIZE}
          totalCount={total}
          currentPage={page}
          onPageChange={setPage}
          searchable={false}
          onRowClick={(row) => { setEditItem(row); setFormOpen(true) }}
        />
      )}

      {/* Form Modal */}
      <ItemFormModal
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v)
          if (!v) setEditItem(null)
        }}
        item={editItem}
        categories={categories}
        hasSyncTenants={syncTenantIds.length > 0}
        defaultUnit={defaultUnit}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['service-catalog-items'] })
        }}
      />

      {/* Delete confirm */}
      {deleteConfirm && (
        <Modal
          open={!!deleteConfirm}
          onOpenChange={() => setDeleteConfirm(null)}
          title="Artikel löschen"
          size="sm"
        >
          <p className="text-sm text-gray-700 mb-4">
            Soll der Artikel <strong>{deleteConfirm.title}</strong> wirklich gelöscht werden?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Abbrechen</Button>
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate(deleteConfirm.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Löschen
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
